import { apiClient } from "@/api";
import { Project, Role, Task } from "@/api/types";
import App from "@/App";
import AppButton from "@/components/structure/AppButton";
import AppLinkButton from "@/components/structure/AppLinkButton";
import SimplePage from "@/components/structure/SimplePage";
import TaskTile from "@/components/tasks/TaskTile";
import { faList, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, motion } from "motion/react";
import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router";

export default function ProjectDetails() {
  const { projectId } = useParams<{ projectId: string }>();

  const [project, setProject] = useState<Project | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  async function fetchProjectDetails() {
    setIsRefreshing(true);
    try {
      const data = await apiClient.get<Project>(`/projects/${projectId}/`);
      setProject(data);
    } catch (err) {
      setError("Failed to fetch project details");
    } finally {
      // add a small delay to let the animation play at least a bit
      setTimeout(() => setIsRefreshing(false), 980 );
    }
  }

  useEffect(() => {
    fetchProjectDetails();
  }, [projectId]);

  // periodically refresh the project details every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchProjectDetails();
    }, 60000);
    return () => clearInterval(interval);
  }, [projectId]);

  // sort the tasks by task.userDependencyStatus.canRespond (those that can respond first), then by the number of dependencies (those with fewer dependencies first), then alphabetically by title
  // use useMemo to avoid re-sorting on every render
  const sortedTasks = useMemo(() => {
    return project?.tasks.sort((a, b) => {
      if (
        a.userDependencyStatus?.canRespond &&
        !b.userDependencyStatus?.canRespond
      ) {
        return -1;
      }
      if (
        !a.userDependencyStatus?.canRespond &&
        b.userDependencyStatus?.canRespond
      ) {
        return 1;
      }
      if ((a.dependencies?.length || 0) < (b.dependencies?.length || 0)) {
        return -1;
      }
      if ((a.dependencies?.length || 0) > (b.dependencies?.length || 0)) {
        return 1;
      }
      return a.title.localeCompare(b.title);
    });
  }, [project]);

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  if (!project) {
    return <div>Loading...</div>;
  }

  return (
    <SimplePage title="Project Details">
      <AppLinkButton variant="primary" to="/" className="mb-4">
        ← Back to Home
      </AppLinkButton>

      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">{project.name}</h1>
        {project.roles?.includes(Role.Facilitator) && (
          <AppButton href={`/projects/${project.id}/edit`} variant="primary">
            Edit Project
          </AppButton>
        )}
      </div>

      <p>{project.description}</p>

      {/* Render tasks or other project details here */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">Tasks</h3>
          <AppButton
            onClick={() => { fetchProjectDetails(); }}
            variant="outline"
          >
            <FontAwesomeIcon icon={faRefresh} spin={isRefreshing} className="mr-2" />
            Refresh Tasks
          </AppButton>
        </div>

        <ul>
          <AnimatePresence mode="popLayout">
            {sortedTasks && sortedTasks.length > 0 ? (
              sortedTasks.map((task: Task) => (
                <motion.div
                  key={`task_frame_${task.id}`}
                  layout
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                >
                  <TaskTile key={`task_${task.id}`} task={task} />
                </motion.div>
              ))
            ) : (
              <li className="text-gray-500">No tasks available</li>
            )}
          </AnimatePresence>
        </ul>
      </div>
    </SimplePage>
  );
}
